import React, { useState } from 'react';
import Button from 'components/general/button/Button';
import DocumentUploader, { DefaultDocument } from 'components/general/document_uploader/DocumentUploader';
import DocumentSection from 'components/general/document_uploader/DocumentSection';
import { getCSRFToken } from 'lib/request';

interface ResumeState {
  uploadedCv?: DefaultDocument;
  selectedCv?: DefaultDocument;
  profileCvs?: DefaultDocument[];
}

interface ResumeStepProps {
  jobId: string;
  isUserAuthenticated: boolean;
  resumeState: ResumeState;
  setResumeState: React.Dispatch<React.SetStateAction<ResumeState>>;
  onNext: () => void;
  onPrevious?: () => void;
  isLastStep?: boolean;
}

const ResumeStep: React.FC<ResumeStepProps> = ({
  jobId,
  isUserAuthenticated,
  resumeState,
  setResumeState,
  onNext,
  onPrevious,
  isLastStep,
}) => {
  const styles = require('./Step.module.scss');
  const [isUploading, setIsUploading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const { uploadedCv, selectedCv, profileCvs } = resumeState;

  const handleResumeSelect = (cv: DefaultDocument) => {
    setResumeState(prev => ({ ...prev, selectedCv: cv }));
  };

  const handleResumeRemove = (cv: DefaultDocument) => {
    setResumeState(prev => ({
      ...prev,
      profileCvs: prev.profileCvs?.filter(c => c.id !== cv.id) || [],
      selectedCv: cv.id === prev.selectedCv?.id ? null : prev.selectedCv,
    }));
  };

  const handleNext = async () => {
    if (!isUserAuthenticated) {
      onNext();
      return;
    }

    if (!uploadedCv && !selectedCv) return;
    if (isUploading || isUpdating) return;

      setIsUpdating(true);
        
      try {
        let cvId: string;
        if (selectedCv) {
          cvId = selectedCv.id
        } else if (uploadedCv) {
          cvId = uploadedCv.id;
        }
        const response = await fetch(window.Routes.update_cv_job_ad_quick_applications_url(jobId), {
          method: "PATCH",
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCSRFToken(),
          },
          body: JSON.stringify({ cv_id: cvId }),
        });

        const data = await response.json();
      if (data.success) onNext();
    } catch {
      // Silently handle error
      } finally {
        setIsUpdating(false);
    }
  };

  const handleFileSelect = async (file: File) => {
    setUploadError("");
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('cv[file]', file, file.name);

      const response = await fetch(window.Routes.upload_cv_job_ad_quick_applications_url(jobId), {
        method: "POST",
        mode: 'cors',
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCSRFToken(),
        },
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      
      if (isUserAuthenticated) {
        const selectedDoc = data.profile_cvs?.find((cv: any) => cv.id === data.selected_cv_id);
        if (selectedDoc) {
          setResumeState(prev => ({
            ...prev,
            uploadedCv: selectedDoc,
            selectedCv: selectedDoc,
            profileCvs: data.profile_cvs?.filter((c: any) => c.id !== selectedDoc.id) || [],
          }));
        }
      } else {
        setResumeState(prev => ({
          ...prev,
          uploadedCv: data.selected_cv,
          selectedCv: data.selected_cv,
        }));
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsUploading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === 'Go') {
      e.preventDefault();
      
      const hasNoCv = !uploadedCv && !selectedCv;
      const hasNoProfileCvs = !profileCvs || profileCvs.length === 0;
      
      if (hasNoCv && hasNoProfileCvs && !isUploading && !isUpdating) {
        const fileInput = document.querySelector('#resume') as HTMLInputElement;
        fileInput?.click();
      } else if (!isUploading && !isUpdating && (uploadedCv || selectedCv)) {
        handleNext();
      }
    }
  };

  return (
    <>
      <div className={styles.documentsList} onKeyDown={handleKeyDown}>
        <DocumentUploader
          id="resume"
          title="Click here to upload your resume"
          subtitle="PDF files only. Maximum file size of 10MB."
          loadingTitle="Uploading your resume..."
          hint={!isUserAuthenticated && !selectedCv && uploadedCv ? "Uploaded from your Landing profile" : ""}
          error={uploadError}
          accept=".pdf"
          disabled={isUploading}
          required={false}
          isMultiple={isUserAuthenticated}
          uploadedDocument={uploadedCv}
          isDocumentSelected={selectedCv?.id === uploadedCv?.id}
          isLoading={isUploading}
          onFileUpload={handleFileSelect}
          onDocumentSelect={handleResumeSelect}
        />
        {isUserAuthenticated && profileCvs && (
          <>
            {profileCvs
              .sort((a, b) => {
                // Selected CV first, then by upload date (newest first)
                if (selectedCv?.id === a.id) return -1;
                if (selectedCv?.id === b.id) return 1;
                return new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime();
              })
              .map((doc) => (
                <DocumentSection
                  key={doc.id}
                  id={doc.id}
                  name={doc.name}
                  uploadDate={doc.uploaded_at}
                  isSelected={selectedCv?.id === doc.id}
                  onSelect={() => handleResumeSelect(doc)}
                  previewUrl={window.Routes?.resume_url?.(doc.id)}
                  deleteUrl={window.Routes?.resume_url?.(doc.id)}
                  onDelete={() => handleResumeRemove(doc)}
                />
              ))}
          </>
        )}

        <p className={styles.privacyNotice}>
          <span>We use Antrophic AI to safely and automatically update your profile. Check our </span>
          <a href="/privacy" className={styles.privacyLink}>Privacy policy</a>
          <span> to know more.</span>
        </p>
      </div>

      <div className={styles.footer}>
        {onPrevious && (
          <Button
            onClick={onPrevious}
            type="Secondary"
            size="M"
            iconLeftName="chevronLeft"
            className={styles.actionButton}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
              }
            }}
          >
            Previous
          </Button>
        )}

        <Button
          onClick={handleNext}
          disabled={isUploading || isUpdating || (!uploadedCv && !selectedCv)}
          size="M"
          iconRightName={isLastStep ? undefined : "chevronRight"}
          className={styles.actionButton}
        >
          {isLastStep ? 'Apply' : 'Next'}
        </Button>
      </div>
    </>
  );
};

export default ResumeStep;
